import React, { useState } from 'react';
import { Box, Button, Flex, Text, useColorModeValue } from '@chakra-ui/react';
import { AddIcon, ArrowBackIcon } from '@chakra-ui/icons';
import ReusableTable from 'components/ReusableTable';
import useDebounce from 'constants/helper/useDebounce';
import { useApplicationGuidelineList } from './hooks/useApplicationGuidelineList';
import { useQueryClient } from '@tanstack/react-query';
import { useApplicationGuidelineDelete } from './hooks/useApplicationGuidelineDelete';
import Swal from 'sweetalert2';
import VideoLinkForm from './videoLinkForm';

const ApplicationGuidelines = () => {
  const [showForm, setShowForm] = useState(false);
  const [page, setPage] = useState(1);
  const [searchString, setSearchString] = useState('');
  const debouncedSearch = useDebounce(searchString, 500);
  const [selectedId, setSelectedId] = useState(null);
  const queryClient = useQueryClient();

  const handleSearch = (value) => setSearchString(value);
  const { applicationGuideline, totalPages, isLoading } =
    useApplicationGuidelineList(page, debouncedSearch);

  const tableData = applicationGuideline?.map((item, index) => ({
    ...item,
    srNo: (page - 1) * 20 + index + 1,
  }));

  const { mutate: deleteLink } = useApplicationGuidelineDelete();
  const swalBackground = useColorModeValue('#ffffff', '#1A202C');
  const swalColor = useColorModeValue('#000000', '#ffffff');

  const handleDelete = (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'This Link will be permanently deleted.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#319795',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
      background: swalBackground,
      color: swalColor,
      borderRadius: '15px',
    }).then((result) => {
      if (result.isConfirmed) {
        deleteLink(id, {
          onSuccess: () => {
            Swal.fire({
              title: 'Deleted!',
              text: 'Link has been deleted successfully.',
              icon: 'success',
              timer: 1500,
              showConfirmButton: false,
              background: swalBackground,
              color: swalColor,
            });

            queryClient.invalidateQueries(['applicationGuidelineList']);
          },
        });
      }
    });
  };

  return (
    <Box p={4} mt={10}>
      {!showForm && (
        <ReusableTable
          data={tableData}
          columns={[
            { key: 'srNo', label: 'Sr.No', width: '80px', headerSize: '14px' },
            {
              key: 'name',
              label: 'Name',
              width: '300px',
              headerSize: '14px',
            },
            {
              key: 'link',
              label: 'Link',
              width: '300px',
              headerSize: '14px',
            },
          ]}
          page={page}
          totalPages={totalPages}
          loading={isLoading}
          onPageChange={setPage}
          showTopAction={true}
          topAction={{
            title: 'Application Guidelines',
            label: 'Add Link',
            icon: <AddIcon />,
            onClick: () => setShowForm(true),
          }}
          showSearch={true}
          onSearch={handleSearch}
          showAddBtn={true}
          showViewToggle={false}
          onEdit={(id) => {
            setSelectedId(id);
            setShowForm(true);
          }}
          onDelete={(row) => handleDelete(row._id)}
        />
      )}

      {showForm && (
        <Box w="100%">
          <Flex
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            gap={4}
            mb={4}
            ml={3}
          >
            <Text fontSize="22px" fontWeight="bold">
              Application Guidelines
            </Text>
            <Button
              variant="ghost"
              leftIcon={<ArrowBackIcon />}
              onClick={() => {
                setShowForm(false);
                setSelectedId(null);
              }}
            >
              Back
            </Button>
          </Flex>

          <VideoLinkForm
            setSelectedId={setSelectedId}
            editId={selectedId}
            onSuccess={() => {
              queryClient.invalidateQueries(['applicationGuidelineList']);
              setShowForm(false);
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default ApplicationGuidelines;
